'use strict';

/**
 * Ordering for trial lists.
 *
 * Ids compare as numbers, so 111 comes before 1099 before 1110.
 * Dates compare by the recorded second (the precision the recordings table
 * shows). Trials in that same second list highest id first.
 */

function finiteId(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function idLabel(trialOrId) {
  if (trialOrId != null && typeof trialOrId === 'object') {
    return trialOrId.id == null ? '' : String(trialOrId.id);
  }
  return trialOrId == null ? '' : String(trialOrId);
}

/** Numeric id from a trial row or a bare id. Null when it is not a finite number. */
export function trialIdNumber(trialOrId) {
  if (trialOrId != null && typeof trialOrId === 'object') return finiteId(trialOrId.id);
  return finiteId(trialOrId);
}

/**
 * Compare trial ids as numbers.
 * `descending` puts the higher id first. Ids that are not numbers sort after
 * numeric ones, then against each other in numeric string order.
 */
export function compareTrialIds(a, b, descending = false) {
  const left = trialIdNumber(a);
  const right = trialIdNumber(b);
  if (left == null && right == null) {
    const cmp = idLabel(a).localeCompare(idLabel(b), undefined, { numeric: true });
    return descending ? -cmp : cmp;
  }
  if (left == null) return 1;
  if (right == null) return -1;
  if (left === right) return 0;
  return descending ? right - left : left - right;
}

/** Higher trial id first. */
export function compareTrialIdsDesc(a, b) {
  return compareTrialIds(a, b, true);
}

/** Recorded instant truncated to the displayed second, or null when unreadable. */
export function recordedAtSecond(trial) {
  const raw = trial && trial.date;
  if (raw == null || raw === '') return null;
  const ms = Date.parse(String(raw).trim().replace(' ', 'T'));
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

/**
 * Chronological order. Missing dates stay last in either direction.
 * Equal seconds break ties by trial id, highest first.
 */
export function compareTrialsByRecordedAt(a, b, ascending = false) {
  const left = recordedAtSecond(a);
  const right = recordedAtSecond(b);
  if (left == null && right == null) return compareTrialIdsDesc(a, b);
  if (left == null) return 1;
  if (right == null) return -1;
  if (left !== right) return ascending ? left - right : right - left;
  return compareTrialIdsDesc(a, b);
}
